import { COLOR_MASK, TYPE_MASK, WHITE_BIT } from './board.ts';
import type { Rules } from './rules.ts';
import type { Position } from './types.ts';

// Zobrist hashing with two independent 32-bit keys: `h1` indexes the AI's transposition
// table and the pair identifies positions for repetition. Variant state (`ext`) and pieces
// in hand are mixed in arithmetically so variants don't need their own key tables.

function splitmix(seed: number) {
  return () => {
    seed = (seed + 0x9e3779b9) | 0;
    let z = seed;
    z = Math.imul(z ^ (z >>> 16), 0x85ebca6b);
    z = Math.imul(z ^ (z >>> 13), 0xc2b2ae35);
    return (z ^ (z >>> 16)) | 0;
  };
}

// Piece keys: [colour slot 0 white / 1 black / 2 neutral][type 0..31][square].
const PIECE_KEYS = 3 * 32 * 64;
const TURN = PIECE_KEYS;
const CASTLE = TURN + 1;
const EP = CASTLE + 16;
const SIZE = EP + 8;

const rand = splitmix(0x0c4e55b7);
const Z1 = new Int32Array(SIZE);
const Z2 = new Int32Array(SIZE);
for (let i = 0; i < SIZE; i++) {
  Z1[i] = rand();
  Z2[i] = rand();
}

function mix(x: number, salt: number): number {
  let z = (x * 0x9e3779b1 + salt) | 0;
  z = Math.imul(z ^ (z >>> 16), 0x85ebca6b);
  z = Math.imul(z ^ (z >>> 13), 0xc2b2ae35);
  return (z ^ (z >>> 16)) | 0;
}

export interface Hash {
  h1: number;
  h2: number;
}

export function hashPosition(rules: Rules, pos: Position): Hash {
  let h1 = 0;
  let h2 = 0;
  const b = pos.board;
  for (let sq = 0; sq < 64; sq++) {
    const p = b[sq];
    if (p === 0) continue;
    const slot = p & COLOR_MASK ? (p & WHITE_BIT ? 0 : 1) : 2;
    const idx = (slot * 32 + (p & TYPE_MASK)) * 64 + sq;
    h1 ^= Z1[idx];
    h2 ^= Z2[idx];
  }
  if (pos.turn) {
    h1 ^= Z1[TURN];
    h2 ^= Z2[TURN];
  }
  h1 ^= Z1[CASTLE + pos.castling];
  h2 ^= Z2[CASTLE + pos.castling];
  if (pos.ep >= 0) {
    h1 ^= Z1[EP + (pos.ep & 7)];
    h2 ^= Z2[EP + (pos.ep & 7)];
  }
  if (pos.hands) {
    for (let i = 0; i < pos.hands.length; i++) {
      const n = pos.hands[i];
      if (n) {
        h1 ^= mix(n, 0x1000 + i);
        h2 ^= mix(n, 0x2000 + i);
      }
    }
  }
  if (pos.ext) {
    const ext = rules.variant.hashExt ? rules.variant.hashExt(pos) : pos.ext;
    for (let i = 0; i < ext.length; i++) {
      h1 ^= mix(ext[i] + 7, 0x3000 + i);
      h2 ^= mix(ext[i] + 7, 0x4000 + i);
    }
  }
  return { h1, h2 };
}

/** String identity of a position for threefold repetition. */
export function positionKey(rules: Rules, pos: Position): string {
  const { h1, h2 } = hashPosition(rules, pos);
  return `${(h1 >>> 0).toString(36)}.${(h2 >>> 0).toString(36)}`;
}
